import {
  SearchableCard,
  SearchableCardOption,
} from "@risalabs_frontend_org/oasis-ui-kit";

/**
 * Cell value shape for {@link SearchableCardCell}. Mirrors the {@link SearchableCard}
 * API but keeps row-level data (options + current selection) under `value`,
 * matching the convention used by other cell types in {@link tableCell}.
 */
export interface SearchableCardCellValue {
  options: SearchableCardOption[];
  value?: string;
  defaultValue?: string;
  label?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  heading?: string;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  size?: "small" | "medium" | "large";
  shouldUsePortal?: boolean;
  id?: string;
}

/**
 * Props for {@link SearchableCardCell}. Follows the same shape as other
 * editable cells (e.g. SelectableBadgeCell, EditableTextCell): the cell
 * forwards user edits via `onValueChange` and a positional
 * `onTableRowUpdate(rowIndex, header, newValue)` so the table can patch row
 * state.
 */
interface SearchableCardCellProps {
  value: SearchableCardCellValue;
  rowIndex: number;
  header: string;
  onValueChange?: (newValue: string, option: SearchableCardOption) => void;
  onTableRowUpdate?: (
    rowIndex: number,
    fieldKey: string,
    newValue: unknown,
  ) => void;
  /** `id` is the cell value's identifier (e.g. row/entity id), forwarded verbatim. */
  onSearchChange?: (searchTerm: string, id: string) => void;
  /** `id` is the cell value's identifier (e.g. row/entity id), forwarded verbatim. */
  onCreateOption?: (value: string, id: string) => void;
  /** `id` is the cell value's identifier (e.g. row/entity id), forwarded verbatim. */
  onOptionClick?: (option: SearchableCardOption, id: string) => void;
}

const SearchableCardCell = ({
  value,
  rowIndex,
  header,
  onValueChange,
  onTableRowUpdate,
  onSearchChange,
  onCreateOption,
  onOptionClick,
}: SearchableCardCellProps) => {
  const handleChange = (
    newValue: string,
    selectedOption: SearchableCardOption,
  ) => {
    onValueChange?.(newValue, selectedOption);
    onTableRowUpdate?.(rowIndex, header, {
      ...value,
      value: newValue,
    });
  };

  const cellId = value?.id ?? "";

  return (
    <div className="w-full">
      <SearchableCard
        options={value?.options ?? []}
        value={value?.value}
        defaultValue={value?.defaultValue}
        label={value?.label}
        placeholder={value?.placeholder}
        searchPlaceholder={value?.searchPlaceholder}
        heading={value?.heading}
        disabled={value?.disabled}
        required={value?.required}
        error={value?.error}
        hint={value?.hint}
        size={value?.size}
        shouldUsePortal={value?.shouldUsePortal ?? true}
        id={value?.id}
        onChange={handleChange}
        onSearchChange={(term) => onSearchChange?.(term, cellId)}
        onCreateOption={(newValue) => onCreateOption?.(newValue, cellId)}
        onOptionClick={(option) => onOptionClick?.(option, cellId)}
        keepTriggerMounted
      />
    </div>
  );
};

export default SearchableCardCell;
